import React from "react";
import { CheckCircle2, XCircle, HelpCircle, History } from "lucide-react";

// A listing's availability from the data provider is a LAST-KNOWN status, not a
// real-time guarantee. We only show a confident "Live" when it was verified
// recently; otherwise we say "Last known …" with the timestamp. UNKNOWN is never
// shown as Live.
export const RECENT_HOURS = 48;

const ageHours = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 36e5;
};

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const h = (Date.now() - d.getTime()) / 36e5;
  if (h < 1) return "just now";
  if (h < 24) return `${Math.round(h)}h ago`;
  const days = Math.round(h / 24);
  if (days <= 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

export const AvailabilityBadge = ({ state, checkedAt, checking, testid }) => {
  if (checking) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] bg-slate-50 text-slate-500 border-slate-200"
        data-testid={testid}>Checking…</span>
    );
  }
  const when = fmtWhen(checkedAt);
  const recent = ageHours(checkedAt) != null && ageHours(checkedAt) <= RECENT_HOURS;

  let cfg;
  if (state === "UNAVAILABLE") {
    cfg = { label: "No longer available", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle,
            cap: when ? `last known ${when}` : "last known status" };
  } else if (state === "LIVE" && recent) {
    cfg = { label: "Live", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2,
            cap: when ? `verified ${when}` : "recently verified" };
  } else if (state === "LIVE") {
    // Provider lists it, but not verified recently — do NOT imply it is live now.
    cfg = { label: "Last known: listed", cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: History,
            cap: when ? `as of ${when}` : "not recently verified" };
  } else {
    cfg = { label: "Availability not verified", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: HelpCircle,
            cap: null };
  }
  const Icon = cfg.Icon;
  return (
    <span className="inline-flex flex-col items-start gap-0.5" data-testid={testid} data-availability-state={state}>
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] ${cfg.cls}`} title={cfg.label}>
        <Icon className="h-3 w-3" /> {cfg.label}
      </span>
      {cfg.cap && <span className="text-[10px] text-muted-foreground" data-testid={testid ? `${testid}-when` : undefined}>{cfg.cap}</span>}
    </span>
  );
};
